import React, { ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Params, useNavigate, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { NavigateFunction } from 'react-router-dom';

import { Failure, Member, Programme, Team } from '../../../types/entity.types';
import { RootState } from '../../../types/state/root.types';
import { TeamState } from '../../../types/state/team.types';
import { MemberState } from '../../../types/state/member.types';
import { ProgrammeState } from '../../../types/state/programme.types';

import {
  requestActivateProgramme,
  requestCreateProgramme,
  requestSendMemberActivationCommunications,
  requestUpdateProgramme,
  requestViewProgramme,
  requestViewUnassignedMembers,
  requestUploadMemberExcel
} from '../../../api/programme/programme.actions';
import {
  requestCreateMember,
  requestDeleteMember,
  requestDownloadMemberExcel,
  requestUpdateMember,
  requestViewAllMembers
} from '../../../api/member/member.actions';
import {
  requestCreateTeam,
  requestViewTeam,
  requestUpdateTeam,
  requestDeleteTeam
} from '../../../api/team/team.actions';
import { requestAddMemberToTeam, requestRemoveMemberFromTeam } from '../../../api/team-member/team-member.actions';
import {
  requestViewTeamsForProgramme,
  requestDownloadTeamExcel,
  requestUploadTeamExcel
} from '../../../api/programme-teams/programme-teams.actions';

import { setProgramme } from '../../../slices/programme/programme.slice';
import { setMembers } from '../../../slices/programme/member/member.slice';
import { setCollections } from '../../../slices/programme/plan/plan.slice';
import { setAccountabilityTemplate } from '../../../slices/programme/accountability/accountability.slice';
import { setSupportTemplate } from '../../../slices/programme/support/support.slice';
import { setTribeTemplate } from '../../../slices/programme/tribe/tribe.slice';
import { setTeam } from '../../../slices/programme/team/team.slice';

import { ARCHIVED, NEW, UPCOMING, TEAMS } from '../../../utils/constants';
import { fileToBase64, downloadExcelFile } from '../../../utils/util';

import General from '../../../components/screens/programme/general/General';

const GeneralContainer = (): ReactElement => {
  const dispatch: Dispatch = useDispatch();

  const navigate: NavigateFunction = useNavigate();

  const { status, programmeExternalReference, view, teamExternalReference }: Params = useParams();

  const { programme } = useSelector(({ programme }: RootState): ProgrammeState => programme);
  const { members, unassignedMembers } = useSelector(({ member }: RootState): MemberState => member);
  const { team, teams } = useSelector(({ team }: RootState): TeamState => team);

  const retrieveUnassignedMembers: () => void = useCallback((): void => {
    // @ts-expect-error TODO: Fix after adding types to programme actions
    dispatch(requestViewUnassignedMembers({ externalReference: programmeExternalReference }));
  }, [dispatch, programmeExternalReference]);

  useEffect((): void => {
    if (status === NEW) {
      dispatch(
        setProgramme({
          name: '',
          description: '',
          fromDate: undefined,
          toDate: undefined,
          theme: undefined,
          isNutrition: true,
          isMovement: true,
          isSleep: true,
          isCondition: true,
          status: UPCOMING
        })
      );
      dispatch(setMembers([]));
      dispatch(setCollections([]));
      dispatch(setAccountabilityTemplate(undefined));
      dispatch(setSupportTemplate(undefined));
      dispatch(setTribeTemplate(undefined));
    }
  }, [status, dispatch]);

  useEffect((): void => {
    if (programmeExternalReference) {
      // @ts-expect-error TODO: Fix after adding types to programme actions
      dispatch(requestViewProgramme({ externalReference: programmeExternalReference }));
    }
  }, [dispatch, programmeExternalReference]);

  useEffect((): void => {
    if (view === TEAMS) {
      // @ts-expect-error TODO: Fix after adding types to programme team actions
      dispatch(requestViewTeamsForProgramme({ externalReference: programmeExternalReference }));
    } else {
      // @ts-expect-error TODO: Fix after adding types to member actions
      dispatch(requestViewAllMembers({ externalReference: programmeExternalReference }));
    }
  }, [dispatch, programmeExternalReference, view]);

  useEffect((): void => {
    if (teamExternalReference) {
      // @ts-expect-error TODO: Fix after adding types to team actions
      dispatch(requestViewTeam({ externalReference: teamExternalReference }));
    } else {
      dispatch(setTeam(undefined));
    }
  }, [dispatch, teamExternalReference]);

  const createProgramme = (programme: Programme, redirectUrl?: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to programme actions
      requestCreateProgramme({
        input: {
          programme
        },
        onSuccess: (externalReference: string): void => {
          if (redirectUrl) {
            navigate(redirectUrl);
          } else {
            navigate(`/programmes/upcoming/${externalReference}/general/members`);
          }
        }
      })
    );
  };

  const updateProgramme = (programme: Programme, redirectUrl?: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to programme actions
      requestUpdateProgramme({
        input: {
          programme
        },
        onSuccess: (externalReference: string): void => {
          if (redirectUrl) {
            navigate(redirectUrl);
          } else {
            navigate(`/programmes/${status}/${externalReference}/general/members`);
          }
        }
      })
    );
  };

  const createMember = (member: Member, onSuccess: () => void): void => {
    // @ts-expect-error TODO: Fix after adding types to member actions
    dispatch(requestCreateMember({ input: { member, programmeExternalReference }, onSuccess }));
  };

  const updateMember = (member: Member, onSuccess?: () => void): void => {
    // @ts-expect-error TODO: Fix after adding types to member actions
    dispatch(requestUpdateMember({ input: { member, programmeExternalReference }, onSuccess }));
  };

  const deleteMember = (externalReference: string, onSuccess: () => void): void => {
    // @ts-expect-error TODO: Fix after adding types to member actions
    dispatch(requestDeleteMember({ externalReference, programmeExternalReference, onSuccess }));
  };

  const downloadMemberExcel = (): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to member actions
      requestDownloadMemberExcel({
        programmeExternalReference,
        onSuccess: (file?: { data: string }): void => downloadExcelFile(file?.data, `${programme.name} - Members.xlsx`)
      })
    );
  };

  const uploadMemberExcel = async (file: File): Promise<void> => {
    const base64String: string = await fileToBase64(file);
    // @ts-expect-error TODO: Fix after adding types to member actions
    dispatch(requestUploadMemberExcel({ programmeExternalReference, base64String }));
  };

  const activateProgramme = (): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to programme actions
      requestActivateProgramme({
        externalReference: programmeExternalReference,
        onSuccess: (): void => navigate(`/programmes/active/${programmeExternalReference}/general/members`)
      })
    );
  };

  const archiveProgramme = (): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to programme actions
      requestUpdateProgramme({
        input: {
          programme: {
            ...programme,
            status: ARCHIVED
          }
        },
        onSuccess: (): void => navigate(`/programmes/archived/${programmeExternalReference}/general/members`)
      })
    );
  };

  const createTeam = (team: Team, onSuccess: () => void): void => {
    // @ts-expect-error TODO: Fix after adding types to team actions
    dispatch(requestCreateTeam({ team, onSuccess }));
  };

  const updateTeam = (team: Team, onSuccess: () => void): void => {
    // @ts-expect-error TODO: Fix after adding types to team actions
    dispatch(requestUpdateTeam({ team, onSuccess }));
  };

  const deleteTeam = (onSuccess: () => void): void => {
    // @ts-expect-error TODO: Fix after adding types to team actions
    dispatch(requestDeleteTeam({ externalReference: teamExternalReference, onSuccess }));
  };

  const addMemberToTeam = (memberExternalReference: string, onSuccess: () => void): void => {
    // @ts-expect-error TODO: Fix after adding types to team member actions
    dispatch(requestAddMemberToTeam({ teamExternalReference, memberExternalReference, onSuccess }));
  };

  const removeMemberFromTeam = (memberExternalReference: string, onSuccess: () => void): void => {
    // @ts-expect-error TODO: Fix after adding types to team member actions
    dispatch(requestRemoveMemberFromTeam({ teamExternalReference, memberExternalReference, onSuccess }));
  };

  const downloadTeamExcel = (): void => {
    // @ts-expect-error TODO: Fix after adding types to programme team actions
    dispatch(requestDownloadTeamExcel({ externalReference: programmeExternalReference }));
  };

  const uploadTeamExcel = async (file: File, onError: (value: Failure[]) => void): Promise<void> => {
    const base64: string = await fileToBase64(file);
    // @ts-expect-error TODO: Fix after adding types to programme team actions
    dispatch(requestUploadTeamExcel({ externalReference: programmeExternalReference, base64, onError }));
  };

  const sendMemberActivationCommunications = (
    memberExternalReference: string,
    sendSms: boolean,
    sendEmail: boolean
  ): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to programme actions
      requestSendMemberActivationCommunications({
        programmeExternalReference,
        memberExternalReference,
        sendSms,
        sendEmail
      })
    );
  };

  return (
    <General
      programme={programme}
      members={members}
      unassignedMembers={unassignedMembers}
      team={team}
      teams={teams}
      createProgramme={createProgramme}
      updateProgramme={updateProgramme}
      createMember={createMember}
      updateMember={updateMember}
      deleteMember={deleteMember}
      downloadMemberExcel={downloadMemberExcel}
      uploadMemberExcel={uploadMemberExcel}
      activateProgramme={activateProgramme}
      archiveProgramme={archiveProgramme}
      createTeam={createTeam}
      updateTeam={updateTeam}
      deleteTeam={deleteTeam}
      retrieveUnassignedMembers={retrieveUnassignedMembers}
      addMemberToTeam={addMemberToTeam}
      removeMemberFromTeam={removeMemberFromTeam}
      uploadTeamExcel={uploadTeamExcel}
      downloadTeamExcel={downloadTeamExcel}
      sendMemberActivationCommunications={sendMemberActivationCommunications}
    />
  );
};

export default GeneralContainer;
